<template>
  <Button
    :class="[$style.base, $style[props.size], $style[props.variation], props.state && $style[props.state]]"
    :as="isAnchorType ? props.type : undefined"
    :size="props.size"
    data-sb-regression="sb-button"
    :severity="variation"
    :link="props.variation === 'link'"
    :icon="calculatedIcon"
    :dt="minimalDt"
    @click="$emit('click', $event)"
  >
    <template v-if="!props.icon">
      <slot v-if="!$attrs.label && props.variation !== 'iconOnly'">
        <sb-text value="common.submit" />
      </slot>
    </template>
    <template
      v-if="isSvgIcon"
      #icon="slotProps"
    >
      <span :class="slotProps.class">
        <sb-svg-icon :svg="props.icon" />
      </span>
    </template>
  </Button>
</template>

<script setup>
/**
 * Buttons are generally used for interface actions. Suitable for all-purpose use.
 * This is an extension to the standard button element with icons and theming.
 */
import { defineProps, computed, defineOptions, defineEmits } from 'vue';
import Button from 'primevue/button';
import buttonProps from '@src/components/Button/props';
import SbSvgIcon from '@src/components/Icons/components/SbSvgIcon.vue';
import SbText from '@src/components/Text/SbText.vue';

defineOptions({
  name: 'SbButtonNext',
});

const props = defineProps(buttonProps);
const variation = computed(() => {
  switch (props.variation) {
    case 'tertiary':
      return 'contrast';
    case 'warning':
      return 'danger';
    case 'minimal':
      return 'primary';
    case 'caution':
      return 'warn';
    case 'link':
    case 'iconOnly':
      return undefined;
    default:
      return props.variation;
  }
});

const isAnchorType = computed(() => props.type === 'a');

const isSvgIcon = computed(() => {
  return !!props.icon && props.icon.includes('</svg>');
});

const calculatedIcon = computed(() => (!isSvgIcon.value ? props.icon : undefined));

const minimalDt = computed(() => {
  return props.variation === 'minimal' ? {
    'primary.background': 'var(--p-white)',
    'primary.color': 'var(--p-primary-color)',
    'primary.border.color': 'var(--p-primary-color)',
  } : {};
});

/**
 * Defines the events emitted by the component.
 * @emits {MouseEvent} click - Emitted when the button is clicked.
 */
defineEmits([
  'click',
]);
</script>

<style lang="scss" module>
@use '@src/components/Button/style/sb-button.module.scss';
</style>
